import { type AxiosError } from "axios";
import { defineMessages, useIntl } from "react-intl";

import { MutationActions, useEntityMutation, QueryKeys } from "@/api/core";
import { useBookmarkStatus } from "@/api/statuses";
import { useApi } from "@/hooks";
import { statusSchema, type Status } from "@/schemas";
import toast from "@/toast";
import { type ApiError } from "@/types/api";

import { updateStatusCache } from "../utils";

const messages = defineMessages({
  unbookmarkSuccess: {
    id: "bookmark.removed",
    defaultMessage: "Bookmarked removed",
  },
  undo: { id: "common.undo", defaultMessage: "Undo" },
  deletedTruth: {
    id: "status.error.deletedTruth",
    defaultMessage: "This Truth has been deleted and is no longer available.",
  },
});

interface Variables {
  statusId: string;
}

function useUnBookmarkStatus() {
  const api = useApi();
  const intl = useIntl();
  const bookmarkStatus = useBookmarkStatus();

  return useEntityMutation({
    globalQueryKey: QueryKeys.statuses.bookmarks(),
    idAttributeFromVariables: "statusId",
    action: MutationActions.DELETE,
    schema: statusSchema,
    mutationFn: (variables: Variables) =>
      api.delete<Status>(`/api/v1/bookmarks/statuses/${variables.statusId}`),
    onMutate(variables: Variables) {
      updateStatusCache(variables.statusId, () => ({
        bookmarked: false,
      }));
    },
    onSuccess(_response, variables: Variables) {
      toast.success(intl.formatMessage(messages.unbookmarkSuccess), {
        actionLabel: messages.undo,
        action: () => bookmarkStatus.mutate({ statusId: variables.statusId }),
      });
    },
    onError(error: AxiosError<ApiError>, variables) {
      const data = error.response?.data;

      updateStatusCache(variables.statusId, () => ({
        bookmarked: true,
      }));

      if (error.response?.status === 404) {
        toast.error(intl.formatMessage(messages.deletedTruth));
      } else if (data?.error) {
        toast.error(data.error);
      }
    },
  });
}

export { useUnBookmarkStatus };
